// Package imports:
import React from 'react';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Component imports:
import Alert from '../../../ui-elements/Alert/Alert';
import Link from '../../../ui-elements/Link/Link';
import Table from '../../../ui-elements/Table/Table';
import VerticalTable from '../../../ui-elements/Table/VerticalTable';
import DisplayBox from '../../DisplayBox/DisplayBox';
// Service imports:
import { convertToPercentage, formatNumber, getTableDate } from '../../../services/utils';
import { IEventReport } from '../../../types/KeldanTypes';
import { IPropertySearchResult } from '../../../types/PropertyTypes';
// Type imports:

interface IProps {
    eventId?: string,
    isExample?: boolean,
    report: IEventReport<IPropertySearchResult>
}

const PropertyReportBody: React.FC<IProps> = ({
    eventId,
    isExample = false,
    report
}) => {
    const padDateString = (date?: string) => {
		if (!date) return '-';
        date = date.replaceAll('/', '.')
		if (date[2] !== '.') date = date.padStart(date.length + 1, '0')
		if (date[5] !== '.') date = date.substring(0,3) + '0' + date.substring(3)
		return date;
	}

    return (
        <>
            <div className='property-page-body'>
                <DisplayBox
                    title='Grunnupplýsingar'
                    asideComponent={
                        <Link
                            targetBlank
                            url={`/Pdf/Fasteignaskra/${eventId}`}
                            disabled={isExample}
                        >
                            <FontAwesomeIcon icon={faFilePdf} /> Sækja skýrslu
                        </Link>
                    }
                    className='property-report-box'
                    grayBackground={isExample}
                >
                    <div className='italic-text'>
                        Skýrsla keypt: {report.eventDate}
                    </div>
                    <div className='report-section'>
                        <div className='vertical-table-wrapper'>
                            <VerticalTable
                                fallbackSymbol
                                data={report.overview.property?.property?.property ?? null}
                                columns={[{
                                    title: 'Notkun',
                                    dataAccessor: ({units}) => units[0].display_usage
                                }]}
                                tableSize='lg'
                            />
                            <VerticalTable
                                fallbackSymbol
                                data={report.overview.property?.property?.property ?? null}
                                columns={[{
                                    title: 'Tryggingafélag',
                                    dataAccessor: ({units}) => units[0].insurance
                                }]}
                                tableSize='lg'
                            />
                        </div>
                    </div>

                    {report.overview.property?.property?.property && report.overview.property.property.property[0]?.owners &&
                        <div className='report-section'>
                            <h4>Eigendur</h4>
                            <Table
                                data={report.overview.property.property.property[0].owners}
                                columns={[{
                                    title: 'Nafn',
                                    dataAccessor: ({name}) => name,
                                    textAlign: 'left'
                                }, {
                                    title: 'Kennitala',
                                    dataAccessor: ({ssn}) => ssn,
                                    textAlign: 'left'
                                }, {
                                    title: 'Hlutfall',
                                    dataAccessor: ({owned_part}) => convertToPercentage(owned_part == null ? null : owned_part/100, true, 1),
                                    textAlign: 'right'
                                }, {
                                    title: 'Kaup',
                                    dataAccessor: ({buy_date}) => getTableDate(buy_date ?? null, 'DD/MM/YYYY', '.'),
                                    textAlign: 'right'
                                }, {
                                    title: 'Afhending',
                                    dataAccessor: ({receive_date}) => getTableDate(receive_date ?? null, 'DD/MM/YYYY', '.'),
                                    textAlign: 'right'
                                }]}
                                tableSize='lg'
                            />
                        </div>
                    }

                    {report.overview.property?.property?.property && report.overview.property.property.property[0].units &&
                        <>
                            {report.overview.property.property.property[0].units.map((u, index) => {
                                return u.details?.map((unit, i) => (
                                    <div className='report-section' key={`unit_${index}_${i}`}>
                                        <h4>{unit.display_usage}</h4>
                                        <div className='vertical-table-wrapper'>
                                            <VerticalTable
                                                data={[unit]}
                                                columns={[{
                                                    title: 'Fastanúmer',
                                                    dataAccessor: ({property_number}) => property_number
                                                }, {
                                                    title: 'Landnúmer',
                                                    dataAccessor: ({land_number}) => land_number
                                                }, {
                                                    title: 'Heitisnúmer',
                                                    dataAccessor: ({name_number}) => name_number
                                                }, {
                                                    title: 'Merking',
                                                    dataAccessor: ({tag}) => tag
                                                }, {
                                                    title: 'Flatarmál',
                                                    dataAccessor: ({area, area_unit}) => `${area} ${area_unit}`
                                                }, {
                                                    title: 'Byggingarár',
                                                    dataAccessor: ({building_year}) => building_year
                                                }, {
                                                    title: 'Herbergi',
                                                    dataAccessor: ({rooms}) => rooms
                                                }, {
                                                    title: 'Byggingarefni',
                                                    dataAccessor: ({building_material}) => building_material
                                                }]}
                                                tableSize='lg'
                                            />
                                            <VerticalTable
                                                data={[unit]}
                                                columns={[{
                                                    title: 'Byggingarstig',
                                                    dataAccessor: ({building_status}) => building_status
                                                }, {
                                                    title: 'Húsmat',
                                                    dataAccessor: ({house_estimation}) => `${formatNumber(house_estimation, '-', 0)} kr.`
                                                }, {
                                                    title: 'Lóðarhlutamat',
                                                    dataAccessor: ({land_estimation}) => `${formatNumber(land_estimation, '-', 0)} kr.`
                                                }, {
                                                    title: 'Fasteignamat',
                                                    dataAccessor: ({property_estimation}) => `${formatNumber(property_estimation, '-', 0)} kr.`
                                                }, {
                                                    title: 'Endurstofnverð',
                                                    dataAccessor: ({reform_price}) => `${formatNumber(reform_price, '-', 0)} kr.`
                                                }, {
                                                    title: 'Brunabótamat',
                                                    dataAccessor: ({burn_estimation}) => `${formatNumber(burn_estimation, '-', 0)} kr.`
                                                }, {
                                                    title: 'Byggingarvísit.d.',
                                                    dataAccessor: ({building_index_date}) => getTableDate(building_index_date ?? null, 'DD/MM/YYYY', '.')
                                                }]}
                                                tableSize='lg'
                                            />
                                        </div>
                                    </div>
                                ));
                            })}
                        </>
                    }
                </DisplayBox>

                {report.overview.bondDetails && 
                    <>
                        <h2 className='bonds-heading'>Veðbandayfirlit</h2>
                        <DisplayBox
                            className='property-report-box'
                        >
                            {report.overview.bondDetails.bondDetails?.bondDetails?.owners?.map((owner, index) => (
                                <div className='report-section' key={`bondowner_${index}`}>
                                    <h4>{owner.name}</h4>
                                    <div className='vertical-table-wrapper'>
                                        <VerticalTable
                                            data={[owner]}
                                            columns={[{
                                                title: 'Kennitala',
                                                dataAccessor: ({ssn}) => ssn
                                            }, {
                                                title: 'Málsaðili',
                                                dataAccessor: ({name}) => name
                                            }, {
                                                title: 'Eignarhlutfall',
                                                dataAccessor: ({owned_part}) => convertToPercentage(owned_part === undefined ? null : owned_part/100, true, 1)
                                            }, {
                                                title: 'Tegund skjals',
                                                dataAccessor: ({document_type}) => document_type
                                            }, {
                                                title: 'Númer',
                                                dataAccessor: ({document_number}) => document_number
                                            }]}
                                            tableSize='lg'
                                        />
                                        <VerticalTable
                                            data={[owner]}
                                            columns={[{
                                                title: 'Staða skjals',
                                                dataAccessor: ({document_status}) => document_status
                                            }, {
                                                title: 'Útgáfudagur',
                                                dataAccessor: ({document_published}) => padDateString(document_published)
                                            }, {
                                                title: 'Móttökudagur',
                                                dataAccessor: ({document_received}) => padDateString(document_received)
                                            }, {
                                                title: 'Embættisnúmer',
                                                dataAccessor: ({office_number}) => office_number
                                            }]}
                                            tableSize='lg'
                                        />
                                    </div>
                                </div>
                            ))}
                            {report.overview.bondDetails.bondDetails?.bondDetails?.bonds?.map((bond, index) => (
                                <div className='report-section' key={`bond_${index}`}>
                                    <h4>{bond.number}. veðréttur</h4>
                                    <div className='vertical-table-wrapper'>
                                        <VerticalTable
                                            data={[bond]}
                                            columns={[{
                                                title: 'Upphæð',
                                                dataAccessor: ({amount, currency}) => `${amount} ${currency}`
                                            }, {
                                                title: 'Myntgengi',
                                                dataAccessor: ({currency_rate}) => currency_rate
                                            }, {
                                                title: 'Númer',
                                                dataAccessor: ({id}) => id
                                            }, {
                                                title: 'Tegund',
                                                dataAccessor: ({type}) => type
                                            }, {
                                                title: 'Verðbinding',
                                                dataAccessor: ({index_status}) => index_status
                                            }]}
                                            tableSize='lg'
                                        />
                                        <VerticalTable
                                            data={[bond]}
                                            columns={[{
                                                title: 'Vísitölustig',
                                                dataAccessor: ({index_state}) => index_state
                                            }, {
                                                title: 'Veðhafi',
                                                dataAccessor: ({loaner}) => loaner
                                            }, {
                                                title: 'Útgáfudagur',
                                                dataAccessor: ({published}) => padDateString(published)
                                            }, {
                                                title: 'Móttökudagur',
                                                dataAccessor: ({received}) => padDateString(received)
                                            }]}
                                            tableSize='lg'
                                        />
                                    </div>
                                </div>  
                            ))}
                            {report.overview.bondDetails.bondDetails?.bondDetails?.primary_allowances &&
                                <div className='report-section'>
                                    <h4>Frumheimildir</h4>
                                    <Table
                                        data={report.overview.bondDetails.bondDetails.bondDetails.primary_allowances}
                                        columns={[{
                                            title: 'Númer',
                                            dataAccessor: ({document_number}) => document_number,
                                            textAlign: 'left'
                                        }, {
                                            title: 'Staða',
                                            dataAccessor: ({document_status}) => document_status,
                                            textAlign: 'left'
                                        }, {
                                            title: 'Tegund',
                                            dataAccessor: ({document_type}) => document_type,
                                            textAlign: 'left'
                                        }, {
                                            title: 'Útgáfudagur',
                                            dataAccessor: ({document_published}) => padDateString(document_published),
                                            textAlign: 'right'
                                        }, {
                                            title: 'Móttökudagur',
                                            dataAccessor: ({document_received}) => padDateString(document_received),
                                            textAlign: 'right'
                                        }]}
                                        tableSize='lg'
                                    />
                                </div>
                            }
                            {report.overview.bondDetails.bondDetails?.bondDetails?.contracts &&
                                <div className='report-section'>
                                    <h4>Efnisatriði</h4>
                                    <Table
                                        data={report.overview.bondDetails.bondDetails.bondDetails.contracts}
                                        columns={[{
                                            title: 'Númer',
                                            dataAccessor: ({document_number}) => document_number,
                                            textAlign: 'left'
                                        }, {
                                            title: 'Staða',
                                            dataAccessor: ({document_status}) => document_status,
                                            textAlign: 'left'
                                        }, {
                                            title: 'Embættisnúmer',
                                            dataAccessor: ({office_number}) => office_number,
                                            textAlign: 'left'
                                        }, {
                                            title: 'Tegund',
                                            dataAccessor: ({document_type}) => document_type,
                                            textAlign: 'left'
                                        }, {
                                            title: 'Lýsing',
                                            dataAccessor: ({description}) => description,
                                            textAlign: 'left'
                                        }]}
                                        tableSize='lg'
                                    />
                                </div>
                            }
                        </DisplayBox>
                    </>
                }
            </div>
            <Alert
                type='info'
                headText='Athugið' 
                text={
                    <>
                        <p>Birtar upplýsingar eru sóttar með tengingu við Fasteignaskrá Íslands. Allar upplýsingar eru veittar samkvæmt starfsreglum Fasteignaskrár og í samræmi við lög og reglur um persónuvernd og meðferð persónuupplýsinga.</p>
                        <p>Reynt er að tryggja að upplýsingar séu réttar en Keldan getur ekki ábyrgst áreiðanleika þeirra.</p>
                    </>
                }
            />
        </>
    );
}

export default PropertyReportBody;